import * as React from 'react';
import * as _ from 'lodash';
import {
  AutoSizer,
  List as ReactVirtualizedList,
  ListRowRenderer,
  CellMeasurerCache,
  CellMeasurer,
  WindowScroller,
} from 'react-virtualized';
import {GridCoreProps} from 'react-virtualized/dist/es/Grid';
import {withStyles} from '@material-ui/core/styles';
import {WithStyles} from '@material-ui/core/styles/withStyles';
import {RowDataType} from './redux/actions';
import TableFilter from './TableFilter/TableFilter';
import {VirtualizedComponentProps} from './consts';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import * as seedrandom from 'seedrandom';
import Img, {IMG_SIZES} from '../../shared/components/Img/Img';
import * as PropTypes from 'prop-types';
import {renderCellContent, getColumnDisplayName} from './VirtualizedHelpers';
import {getImageFallbackComponent} from '../../MyAccount/ViewDesigns/helpers/resizeImageUrlHelper';
const THUMBNAIL_SIZE = 100;
// This is an exception, hard coded class key should be avoided
type VirtualizedListClassKey =
  | 'root'
  | 'listRow'
  | 'container'
  | 'list'
  | 'listRowDetails'
  | 'skeleton'
  | 'hoverActions';

type VirtualizedListPropsWithStyles<RowData extends RowDataType> = VirtualizedComponentProps<RowData> &
  WithStyles<VirtualizedListClassKey>;

class VirtualizedList<RowData extends RowDataType> extends React.PureComponent<
  VirtualizedListPropsWithStyles<RowData>,
  {hoverIndex: number}
> {
  state = {
    hoverIndex: null,
  };

  static contextTypes = {
    getScrollableContainer: PropTypes.func.isRequired,
  };

  private virtualizedComponentRef: ReactVirtualizedList;
  private cache = new CellMeasurerCache({
    fixedWidth: true,
  });
  rowRenderer: ListRowRenderer = ({parent, index, key, style}) => {
    const {
      columnsConfig: {title: titleColumnName, thumbnail: thumbnailColumnName},
      hoverActionsRenderer,
      data,
      columns,
      classes: {hoverActions, listRow, skeleton, listRowDetails},
    } = this.props;
    const {hoverIndex} = this.state;
    const rowData = data.get(index) as RowData | null;
    const thumbnailCellData = rowData[thumbnailColumnName];
    const titleCellData = renderCellContent(rowData, titleColumnName as string);
    return (
      <CellMeasurer cache={this.cache} rowIndex={index} columnIndex={0} key={key} parent={parent}>
        <Paper
          onMouseEnter={() => this.handleMouseEnter(index)}
          onMouseLeave={this.handleMouseLeave}
          style={style}
          className={listRow}
        >
          <Img
            size={IMG_SIZES.Thumbnail}
            style={{width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE}}
            src={thumbnailCellData ? (thumbnailCellData as string) : undefined}
            unloader={getImageFallbackComponent(THUMBNAIL_SIZE)}
          />
          <div className={listRowDetails}>
            <Typography component="div" variant="body2">
              {titleCellData ? (
                titleCellData
              ) : (
                <span
                  className={skeleton}
                  style={{
                    width: 50 + seedrandom('table' + index + 'title')() * 100,
                  }}
                />
              )}
            </Typography>
            {_.map(_.omit(columns, 'Title'), (columnInfo, columnName) => {
              const cellData = renderCellContent(rowData, columnName, columnInfo);
              return cellData ? (
                <Typography component="div" variant="body2">
                  {getColumnDisplayName(columnName, columnInfo)}: {cellData}
                </Typography>
              ) : (
                undefined
              );
            })}
          </div>
          {hoverIndex === index && hoverActionsRenderer ? (
            <div className={hoverActions}>{hoverActionsRenderer(rowData)}</div>
          ) : (
            undefined
          )}
        </Paper>
      </CellMeasurer>
    );
  };

  private handleMouseEnter = (hoverIndex: number) => {
    this.setState({hoverIndex});
    this.virtualizedComponentRef.forceUpdateGrid();
  };

  private handleMouseLeave = () => {
    this.setState({hoverIndex: null});
    this.virtualizedComponentRef.forceUpdateGrid();
  };

  private handleSectionRendered: GridCoreProps['onSectionRendered'] = ({rowStartIndex, rowStopIndex}) => {
    const {onRowsRendered} = this.props;
    if (onRowsRendered) {
      onRowsRendered({startIndex: rowStartIndex, stopIndex: rowStopIndex});
    }
  };

  render() {
    // noinspection JSUnusedLocalSymbols
    const {
      hoverActionsRenderer,
      filter,
      filterProps,
      onToggleSelectAll,
      noContentRenderer,
      onChange,
      sortProps,
      onRequestSort,
      columns,
      count,
      virtualizedComponentRef,
      classes: {root, container, list},
      ...otherProps
    } = this.props;
    const {getScrollableContainer} = this.context;
    return (
      <Grid
        container
        item
        xs={12}
        lg={10}
        direction="column"
        alignContent="stretch"
        alignItems="stretch"
        className={root}
      >
        <Grid item>
          <TableFilter count={count} columns={columns} filterValues={filter} {...filterProps} />
        </Grid>
        {count === 0 ? (
          noContentRenderer(this.props)
        ) : count ? ( // when undefined render nothing.
          <Grid item className={container}>
            <WindowScroller scrollElement={getScrollableContainer()}>
              {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
                <AutoSizer disableHeight>
                  {({width}) => (
                    <div ref={registerChild}>
                      <ReactVirtualizedList
                        ref={ref => {
                          this.virtualizedComponentRef = ref;
                          virtualizedComponentRef(ref);
                        }}
                        autoHeight
                        className={list}
                        height={height}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        rowCount={count}
                        rowHeight={this.cache.rowHeight}
                        columnWidth={width}
                        width={width}
                        rowRenderer={this.rowRenderer}
                        scrollTop={scrollTop}
                        {...otherProps}
                        onSectionRendered={this.handleSectionRendered}
                      />
                    </div>
                  )}
                </AutoSizer>
              )}
            </WindowScroller>
          </Grid>
        ) : (
          undefined
        )}
      </Grid>
    );
  }
}

const keyFrameId = `fadeInAndOut${Math.random()
  .toString(36)
  .substring(7)}`;

export default withStyles<VirtualizedListClassKey, {}>(theme => ({
  root: {
    flexGrow: 1,
    flexWrap: 'nowrap',
    flexShrink: 1,
    display: 'flex',
    flexDirection: 'column' as 'column',
    paddingBottom: theme.spacing(8),
  },
  container: {
    flexGrow: 1,
    flexShrink: 1,
    '& .ReactVirtualized__Grid__innerScrollContainer': {
      overflow: 'visible!important' as 'visible',
    },
  },
  listRow: {
    display: 'flex',
    left: 0,
    right: 0,
    alignItems: 'center',
    padding: theme.spacing(1),
    '&:before': {
      position: 'absolute',
      left: 0,
      top: -1,
      right: 0,
      height: 1,
      content: '""',
      opacity: 1,
      backgroundColor: theme.palette.divider,
    },
  },
  listRowDetails: {
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(1),
    height: theme.spacing(15),
  },
  listRowActions: {
    padding: theme.spacing(1),
  },
  list: {
    overflow: 'visible!important' as 'visible',
  },
  [`@keyframes ${keyFrameId}`]: {
    from: {opacity: 0.3},
    to: {opacity: 0.6},
  } as React.CSSProperties,
  skeleton: {
    backgroundColor: theme.palette.text.hint,
    borderRadius: theme.spacing(0.5),
    height: theme.spacing(1),
    animation: `${keyFrameId} 500ms infinite ${theme.transitions.easing.easeInOut} alternate`,
    display: 'inline-block',
  },
  hoverActions: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    padding: theme.spacing(1),
    display: 'flex',
  },
}))(VirtualizedList);
